import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import {
  EventBasicInfo,
  EventHeader,
} from "../../../../components/Events/Event";
import EventRoles from "../../../../components/Events/Event/Roles";
import EventZones from "../../../../components/Events/Event/Zones";
import {
  ZONE_APPLICANTS_PORTALS,
  ZONE_APPLICANTS_URL_PARAMS,
} from "../../../../components/Events/Event/ZoneApplicants/portal";
import RequirementsComments from "../../../../components/Events/Event/RequirementsComments";
import EvaluationRounds from "../../../../components/Events/Event/Evaluations";
import ApplicantsTabV2 from "../ViewEvent/ApplicantsTabV2";
import TimesheetTab from "../ViewEvent/TimesheetTab";
import Reports from "../../Reports";
import PayrollTab from "../ViewEvent/PayrollTab";
import EventHeatMap from "../../../../components/Events/Event/HeatMap/EventHeatMap";
import AdminEventClientReportsTab from "./AdminEventClientReportsTab";
import { TIMESHEET_URL_PARAMS } from "../../../../components/Events/Event/timesheet/constants";
import { PAYROLL_URL_PARAMS } from "../../../../components/Events/Event/Payroll/GeneratePayroll/constants/payrollUrlParams";
import { PAYROLL_GENERATE_ALL } from "../../../../components/Events/Event/Payroll/GeneratePayroll/utils/mapPayrollReportData";
import messages from "../../../../assets/locale/messages";
import {
  cancelEventRequest,
  deleteEventRequest,
  getEventRequest,
  getEventResponse,
} from "../../../../store/Events/actions";
import { getPermissionRequest } from "../../../../store/Auth/actions";
import { setCreateEventFormStep } from "../../../../store/Routing/actions";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { eventStatuses } from "../../../../utils/Constants";
import {
  shouldShowEventApplicantsTab,
  shouldShowEventEvaluationsTab,
  shouldShowEventPayrollTab,
} from "../../../../utils/companyPortal/eventEditRules";
import useEventViewBreadcrumbs from "../../../../hooks/useEventViewBreadcrumbs";
import { useAdminModulePermission } from "../../../../utils/adminPortalHelpers";
import { adminPermissionModules } from "../../../../utils/Constants";
import {
  EVENT_VIEW_DEFAULT_TAB,
  EVENT_VIEW_TABS,
  getAllowedEventViewTabs,
  getEventViewTabFromSearch,
  getEventViewTabLabels,
  isEventPayrollTabEnabled,
  resolveEventViewTabFromSearch,
} from "../../../../utils/eventViewTabs";
import {
  isAdmin,
  isSuperAdmin,
  isSupervisorManger,
  isUser,
  isVisitor,
} from "../../../../utils/Permissions";
import { UIActionModal, UIButton, UITabs } from "../../../../components/ui";
import store from "../../../../store";

import "./ViewEventV2.scss";

const tabToCreateStep = {
  [EVENT_VIEW_TABS.basicInfo]: 0,
  [EVENT_VIEW_TABS.roles]: 1,
  [EVENT_VIEW_TABS.zones]: 2,
  [EVENT_VIEW_TABS.requirementsAndComments]: 2,
  [EVENT_VIEW_TABS.applicants]: 0,
  [EVENT_VIEW_TABS.payroll]: 0,
  [EVENT_VIEW_TABS.timesheet]: 0,
};

const ViewEventV2 = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { state: locationState } = location;

  const lang = useSelector((state) => state.locale.lang);
  const event = useSelector((state) => state.events.event);
  const step = useSelector((state) => state.routing.currentCreateEventFormStep);
  const { events, shared } = messages[lang];
  const { id: userId } = store.getState().auth.login.role;

  const [selectedRound, setSelectedRound] = useState(
    locationState?.selectedRound ?? null,
  );
  const [openActionModal, setOpenActionModal] = useState(false);
  const [modalType, setModalType] = useState(null);

  const eventsPerm = useAdminModulePermission(adminPermissionModules.events);
  const joiningEventsPerm = useAdminModulePermission(
    adminPermissionModules.joiningEvents,
  );
  const timesheetPerm = useAdminModulePermission(
    adminPermissionModules.timesheet,
  );
  const reportsPerm = useAdminModulePermission(
    adminPermissionModules.manageReports,
  );
  const payrollPerm = useAdminModulePermission(adminPermissionModules.payroll);
  const isSupervisor = isSupervisorManger();

  useEffect(() => {
    dispatch(getEventRequest(id));
    return () => {
      dispatch(getEventResponse(null));
    };
  }, [dispatch, id, lang]);

  useEffect(() => {
    if (!isSuperAdmin() && !isAdmin() && !isSupervisorManger()) {
      dispatch(getPermissionRequest({ id: userId }));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (step !== 1 && step !== 2) {
      dispatch(setCreateEventFormStep(0));
      return;
    }

    const nextTab = step === 2 ? EVENT_VIEW_TABS.zones : EVENT_VIEW_TABS.roles;
    const params = new URLSearchParams(location.search);
    params.set("tab", nextTab);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
    dispatch(setCreateEventFormStep(0));
  }, [dispatch, history, location.pathname, location.search, step]);

  const showApplicantsTab = useMemo(() => {
    if (isSupervisor) {
      return false;
    }
    if (!event?.status || isUser()) {
      return false;
    }
    if (!shouldShowEventApplicantsTab(event)) {
      return false;
    }
    if (isVisitor() && !joiningEventsPerm.canView) {
      return false;
    }
    return true;
  }, [event, isSupervisor, joiningEventsPerm]);

  const showEvaluationsTab = isSupervisor ? true : shouldShowEventEvaluationsTab(event);

  const showPayrollTab = useMemo(() => {
    if (!isEventPayrollTabEnabled) return false;
    if (!shouldShowEventPayrollTab(event)) return false;
    if (isSuperAdmin() || isAdmin()) return true;
    if (isVisitor()) return Boolean(payrollPerm.canView);
    return true;
  }, [event, payrollPerm.canView]);

  const showTimesheetTab = useMemo(() => {
    if (!showApplicantsTab) return false;
    if (isSuperAdmin() || isAdmin()) return true;
    if (isVisitor()) return Boolean(timesheetPerm.canView);
    return true;
  }, [showApplicantsTab, timesheetPerm]);

  const showReportsTab = useMemo(() => {
    if (isSupervisor) return false;
    if (isSuperAdmin() || isAdmin()) return true;
    if (isVisitor()) return Boolean(reportsPerm.canView);
    return true;
  }, [isSupervisor, reportsPerm]);

  const showHeatMapTab = !isSupervisor && isSuperAdmin();

  // Read-only Client Reports tab (PROLINE-3632). Gated by `Manage Reports`
  // `can_view` — the list endpoint answers 403 without it.
  const showClientReportsTab = useMemo(() => {
    if (isSupervisor) return false;
    if (isSuperAdmin() || isAdmin()) return true;
    if (isVisitor()) return Boolean(reportsPerm.canView);
    return true;
  }, [isSupervisor, reportsPerm]);

  const allowedTabValues = useMemo(
    () =>
      getAllowedEventViewTabs(
        showApplicantsTab,
        showEvaluationsTab,
        isSupervisor,
        showTimesheetTab,
        showReportsTab,
        showPayrollTab,
        showHeatMapTab,
        showClientReportsTab,
      ),
    [
      isSupervisor,
      showApplicantsTab,
      showEvaluationsTab,
      showTimesheetTab,
      showReportsTab,
      showPayrollTab,
      showHeatMapTab,
      showClientReportsTab,
    ],
  );

  const payrollGenerateUserIdParam =
    new URLSearchParams(location.search).get(PAYROLL_URL_PARAMS.generate) || "";
  const isGeneratingPayroll =
    new URLSearchParams(location.search).get("tab") === EVENT_VIEW_TABS.payroll &&
    Boolean(payrollGenerateUserIdParam);
  const isSingleApplicantPayroll =
    isGeneratingPayroll &&
    payrollGenerateUserIdParam !== PAYROLL_GENERATE_ALL &&
    !payrollGenerateUserIdParam.includes(",");
  const payrollGenerateLabel = isGeneratingPayroll
    ? isSingleApplicantPayroll
      ? events.viewEvents.payrollTab.generatePayroll.singleTitle
      : events.viewEvents.payrollTab.generatePayroll.bulkTitle
    : undefined;

  const { breadcrumbList, activeTab } = useEventViewBreadcrumbs({
    event,
    events,
    eventsListPath: ROUTE_PATHS.events,
    showApplicantsTab,
    showEvaluationsTab,
    showPayrollTab,
    showTimesheetTab,
    showReportsTab,
    showHeatMapTab,
    showClientReportsTab,
    isSupervisor,
    timesheetPortal: "admin",
    selectedRound,
    onEvaluationsBack: () => setSelectedRound(null),
    nestedDetailLabel: payrollGenerateLabel,
  });

  const [hideTabNav, setHideTabNav] = useState(false);

  useEffect(() => {
    if (activeTab !== EVENT_VIEW_TABS.payroll) {
      setHideTabNav(false);
    }
  }, [activeTab]);

  // useLayoutEffect (not useEffect): this resolves `locationState.initialTab`
  // into the URL's `tab` param. useEffect runs after the browser paints, so the
  // first frame would render with no `tab` in the URL yet — flashing the
  // default tab (Basic Info) before this correction lands on the requested one.
  useLayoutEffect(() => {
    if (getEventViewTabFromSearch(location.search, allowedTabValues)) {
      return;
    }

    // Wait for a loaded event before defaulting. Store starts as `event: {}`,
    // so `!event` is never true on refresh — use `id`. Resetting early would
    // wipe `tab` / `applicant` (refresh, language switch, refetch).
    // Supervisors are exempt: their allowed tabs ([basicInfo, evaluations])
    // never depend on event data, so waiting here only flashes Basic Info
    // before this effect corrects to the requested tab once the event loads.
    if (!event?.id && !isSupervisor) {
      return;
    }

    // A requested initialTab (e.g. navigating to Evaluations from the
    // applicant result view) may not be allowed until the event has loaded and
    // the applicants/evaluations tabs become available — wait before defaulting.
    if (
      locationState?.initialTab &&
      !allowedTabValues.includes(locationState.initialTab)
    ) {
      return;
    }

    const desiredTab = allowedTabValues.includes(locationState?.initialTab)
      ? locationState.initialTab
      : EVENT_VIEW_DEFAULT_TAB;

    const params = new URLSearchParams(location.search);
    params.set("tab", desiredTab);
    if (desiredTab !== EVENT_VIEW_TABS.timesheet) {
      params.delete(TIMESHEET_URL_PARAMS.applicant);
    }
    if (desiredTab !== EVENT_VIEW_TABS.payroll) {
      params.delete(PAYROLL_URL_PARAMS.generate);
    }
    if (desiredTab !== EVENT_VIEW_TABS.zones) {
      params.delete(ZONE_APPLICANTS_URL_PARAMS.zone);
    }
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [
    allowedTabValues,
    event,
    history,
    isSupervisor,
    location.pathname,
    location.search,
    locationState,
  ]);

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) {
      return;
    }

    setSelectedRound(null);
    const params = new URLSearchParams(location.search);
    params.set("tab", nextTab);
    if (nextTab !== EVENT_VIEW_TABS.timesheet) {
      params.delete(TIMESHEET_URL_PARAMS.applicant);
    }
    if (nextTab !== EVENT_VIEW_TABS.payroll) {
      params.delete(PAYROLL_URL_PARAMS.generate);
    }
    if (nextTab !== EVENT_VIEW_TABS.zones) {
      params.delete(ZONE_APPLICANTS_URL_PARAMS.zone);
    }
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const tabs = useMemo(() => {
    const labels = getEventViewTabLabels(events.viewEvents);
    const next = isSupervisor
      ? [
          { value: EVENT_VIEW_TABS.basicInfo, label: labels.basicInfo },
          { value: EVENT_VIEW_TABS.evaluations, label: labels.evaluations },
        ]
      : [
          { value: EVENT_VIEW_TABS.basicInfo, label: labels.basicInfo },
          { value: EVENT_VIEW_TABS.roles, label: labels.roles },
          { value: EVENT_VIEW_TABS.zones, label: labels.zones },
          {
            value: EVENT_VIEW_TABS.requirementsAndComments,
            label: labels.requirementsAndComments,
          },
          showApplicantsTab
            ? { value: EVENT_VIEW_TABS.applicants, label: labels.applicants }
            : null,
          showPayrollTab
            ? { value: EVENT_VIEW_TABS.payroll, label: labels[EVENT_VIEW_TABS.payroll] }
            : null,
          showEvaluationsTab
            ? { value: EVENT_VIEW_TABS.evaluations, label: labels.evaluations }
            : null,
          showTimesheetTab
            ? { value: EVENT_VIEW_TABS.timesheet, label: labels.timesheet }
            : null,
          showReportsTab
            ? { value: EVENT_VIEW_TABS.reports, label: labels.reports }
            : null,
          showHeatMapTab
            ? { value: EVENT_VIEW_TABS.heatMap, label: labels.heatMap }
            : null,
          showClientReportsTab
            ? {
                value: EVENT_VIEW_TABS.clientReports,
                label: labels.clientReports,
              }
            : null,
        ].filter(Boolean);

    // While the event is loading, applicants/timesheet are not in the list yet.
    // Keep the URL tab in the strip so MUI does not fall back to basicInfo.
    const urlTab = resolveEventViewTabFromSearch(
      location.search,
      allowedTabValues,
    );
    if (urlTab && !next.some((tab) => tab.value === urlTab)) {
      next.push({ value: urlTab, label: labels[urlTab] });
    }

    return next;
  }, [
    allowedTabValues,
    events.viewEvents,
    isSupervisor,
    location.search,
    showApplicantsTab,
    showEvaluationsTab,
    showPayrollTab,
    showTimesheetTab,
    showReportsTab,
    showHeatMapTab,
    showClientReportsTab,
  ]);

  const canEdit =
    id &&
    [
      eventStatuses.upcoming,
      eventStatuses.pending,
      eventStatuses.ongoing,
    ].includes(event?.status) &&
    (isSuperAdmin() || (isVisitor() && eventsPerm.canEdit));

  const canDelete =
    (event?.status === eventStatuses.pending && isSuperAdmin()) ||
    (isVisitor() && eventsPerm.canDelete);

  const canCancel = event?.status === eventStatuses.upcoming && isSuperAdmin();

  const handleEditEvent = () => {
    history.push({
      pathname: ROUTE_PATHS.editEvent.replace(":id", event.id),
      state: { initialStep: tabToCreateStep[activeTab] ?? 0 },
    });
  };

  const handleAddZone = () => {
    if (!event?.id) return;
    history.push({
      pathname: ROUTE_PATHS.editEvent.replace(":id", event.id),
      state: { initialStep: tabToCreateStep[EVENT_VIEW_TABS.zones] },
    });
  };

  const handleAddRole = () => {
    if (!event?.id) return;
    history.push({
      pathname: ROUTE_PATHS.editEvent.replace(":id", event.id),
      state: { initialStep: tabToCreateStep[EVENT_VIEW_TABS.roles] },
    });
  };

  const handleConfirmModalAction = () => {
    if (modalType === "delete") {
      dispatch(deleteEventRequest(id));
    } else {
      dispatch(cancelEventRequest(id));
    }
    setOpenActionModal(false);
  };

  const openModal = (type) => {
    setModalType(type);
    setOpenActionModal(true);
  };

  const headerBeforeEditActions =
    canDelete || canCancel ? (
      <>
        {canDelete ? (
          <UIButton
            type="button"
            variant="outlined"
            appearance="outline"
            size="large"
            className="admin-event-view-v2__header-outline-action"
            onClick={() => openModal("delete")}
          >
            {events.viewEvents.deleteBtn}
          </UIButton>
        ) : null}
        {canCancel ? (
          <UIButton
            type="button"
            variant="outlined"
            appearance="outline"
            color="inherit"
            size="large"
            onClick={() => openModal("cancel")}
          >
            {events.viewEvents.cancelBtn}
          </UIButton>
        ) : null}
      </>
    ) : null;

  return (
    <Box className="admin-event-view-v2">
      <Breadcrumbs
        list={breadcrumbList}
        className="admin-event-view-v2__breadcrumbs"
        BreadCrumbHasBackIcon={false}
      />

      {!isGeneratingPayroll && (
        <EventHeader
          event={event}
          editLabel={events.viewEvents.editBtn}
          onEdit={handleEditEvent}
          editDisabled={!event?.id}
          showEditButton={Boolean(canEdit)}
          beforeEditAction={headerBeforeEditActions}
        />
      )}

      <UITabs
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons={false}
        className={hideTabNav ? "ui-tabs--nav-hidden" : ""}
        classNames={{ root: "admin-event-view-v2__tabs" }}
        rootSx={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-16)",
        }}
      >
        {activeTab === EVENT_VIEW_TABS.basicInfo && (
          <EventBasicInfo event={event} />
        )}
        {activeTab === EVENT_VIEW_TABS.roles && (
          <EventRoles
            event={event}
            onAddRole={canEdit ? handleAddRole : undefined}
          />
        )}
        {activeTab === EVENT_VIEW_TABS.zones && (
          <EventZones
            event={event}
            onAddZone={canEdit ? handleAddZone : undefined}
            canViewApplicants
            readOnly={!canEdit}
            portalVariant={ZONE_APPLICANTS_PORTALS.admin}
          />
        )}
        {activeTab === EVENT_VIEW_TABS.requirementsAndComments && (
          <RequirementsComments event={event} />
        )}
        {activeTab === EVENT_VIEW_TABS.applicants && showApplicantsTab && (
          <ApplicantsTabV2 />
        )}
        {activeTab === EVENT_VIEW_TABS.payroll && showPayrollTab && (
          <PayrollTab onNestedViewChange={setHideTabNav} />
        )}
        {activeTab === EVENT_VIEW_TABS.evaluations && showEvaluationsTab && (
          <EvaluationRounds
            event={event}
            selectedRound={selectedRound}
            onRoundSelect={setSelectedRound}
            readOnly={!isSupervisor}
            portalVariant="admin-portal"
          />
        )}
        {activeTab === EVENT_VIEW_TABS.timesheet && showTimesheetTab && (
          <TimesheetTab />
        )}
        {activeTab === EVENT_VIEW_TABS.reports && showReportsTab && (
          <Reports embedded />
        )}
        {activeTab === EVENT_VIEW_TABS.heatMap && showHeatMapTab && (
          <EventHeatMap event={event} portalVariant="admin-portal" />
        )}
        {activeTab === EVENT_VIEW_TABS.clientReports && showClientReportsTab && (
          <AdminEventClientReportsTab event={event} />
        )}
      </UITabs>

      <UIActionModal
        open={openActionModal}
        onClose={() => setOpenActionModal(false)}
        title={events.viewEvents.confirmation || shared.confirm}
        variant={modalType === "delete" || modalType === "cancel" ? "reject" : "approve"}
        actions={
          <UIActionModal.Actions
            cancelLabel={shared.cancel}
            confirmLabel={shared.confirm}
            confirmVariant={
              modalType === "delete" || modalType === "cancel"
                ? "reject"
                : "approve"
            }
            onCancel={() => setOpenActionModal(false)}
            onConfirm={handleConfirmModalAction}
          />
        }
      >
        <Typography className="admin-event-view-v2__modal-copy">
          {modalType === "delete"
            ? events.viewEvents.deleteModalConfirmMessage
            : events.viewEvents.cancelModalConfirmMessage}
        </Typography>
      </UIActionModal>
    </Box>
  );
};

export default ViewEventV2;
